import { Avatar, Box, IconButton, Typography } from "@mui/material";
import { MoreHoriz, Close, FavoriteBorder, SendOutlined } from "@mui/icons-material";
import { getMediaKind } from "../util";

interface Props {
  media?: string[];
  username?: string;
  timeLabel?: string;
  avatarUrl?: string;
  caption?: string;
}

export default function InstagramStoriesPreview({
  media = [],
  username = "Your Account",
  timeLabel = "27 s",
  avatarUrl,
  caption
}: Props) {
  const first = media[0];
  const isVideo = first ? getMediaKind(first) === "video" : false;

  return (
    <Box sx={styles.root}>
      {first ? (
        <Box
          component={isVideo ? "video" : "img"}
          src={first}
          autoPlay={isVideo ? true : undefined}
          muted={isVideo ? true : undefined}
          loop={isVideo ? true : undefined}
          playsInline={isVideo ? true : undefined}
          sx={styles.media}
        />
      ) : (
        <Box sx={styles.noMedia}>
          <Typography variant="body2">No media for Stories</Typography>
        </Box>
      )}

      <Box sx={styles.topGradient} />

      <Box sx={styles.progressBar}>
        <Box sx={styles.progressTrack}>
          <Box sx={styles.progressFill} />
        </Box>
      </Box>

      <Box sx={styles.header}>
        <Avatar src={avatarUrl} sx={styles.avatar}>
          {username?.[0]?.toUpperCase() ?? "U"}
        </Avatar>
        <Box sx={styles.headerText}>
          <Typography sx={styles.username} component="span">{username}</Typography>
          <Typography sx={styles.timeLabel} component="span">{timeLabel}</Typography>
        </Box>
        <IconButton size="small" sx={styles.iconBtn}>
          <MoreHoriz fontSize="small" />
        </IconButton>
        <IconButton size="small" sx={styles.iconBtn}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={styles.bottomGradient} />

      {caption ? (
        <Box sx={styles.captionBox}>
          <Typography sx={styles.caption}>{caption}</Typography>
        </Box>
      ) : null}

      <Box sx={styles.bottomBar}>
        <Box sx={styles.inputPill}>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            Send a message...
          </Typography>
        </Box>
        <IconButton size="small" sx={styles.bottomBtn}>
          <FavoriteBorder />
        </IconButton>
        <IconButton size="small" sx={styles.bottomBtn}>
          <SendOutlined />
        </IconButton>
      </Box>
    </Box>
  );
}

const styles = {
  root: {
    position: "relative",
    width: "100%",
    maxWidth: 360,
    aspectRatio: "9 / 16",
    bgcolor: "black",
    borderRadius: 2,
    overflow: "hidden",
    color: "common.white",
    boxShadow: 1
  },
  media: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    display: "block",
    backgroundColor: "black"
  },
  noMedia: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "grey.500"
  },
  topGradient: {
    pointerEvents: "none",
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 140,
    background:
      "linear-gradient(180deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.35) 50%, rgba(0,0,0,0) 100%)"
  },
  bottomGradient: {
    pointerEvents: "none",
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: 160,
    background:
      "linear-gradient(0deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.35) 50%, rgba(0,0,0,0) 100%)"
  },
  progressBar: {
    position: "absolute",
    top: 8,
    left: 8,
    right: 8
  },
  progressTrack: {
    height: 2,
    bgcolor: "rgba(255,255,255,0.35)",
    borderRadius: 9999
  },
  progressFill: {
    width: "65%",
    height: "100%",
    bgcolor: "common.white",
    borderRadius: 9999
  },
  header: {
    position: "absolute",
    top: 16 + 8,
    left: 8,
    right: 8,
    display: "flex",
    alignItems: "center",
    gap: 1
  },
  avatar: {
    width: 28,
    height: 28,
    bgcolor: "#1976d2",
    fontWeight: 700
  },
  headerText: {
    flexGrow: 1,
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: 1
  },
  username: {
    fontWeight: 600,
    whiteSpace: "nowrap"
  },
  timeLabel: {
    opacity: 0.9,
    fontSize: "0.8rem"
  },
  iconBtn: {
    color: "common.white"
  },
  captionBox: {
    position: "absolute",
    left: 12,
    right: 12,
    bottom: 88
  },
  caption: {
    variant: "body2",
    whiteSpace: "pre-wrap"
  },
  bottomBar: {
    position: "absolute",
    left: 12,
    right: 12,
    bottom: 12,
    display: "flex",
    alignItems: "center",
    gap: 1
  },
  inputPill: {
    flexGrow: 1,
    borderRadius: 9999,
    bgcolor: "rgba(255,255,255,0.1)",
    border: "1px solid rgba(255,255,255,0.3)",
    px: 2,
    py: 0.75
  },
  bottomBtn: {
    color: "common.white",
    bgcolor: "rgba(255,255,255,0.08)",
    "&:hover": { bgcolor: "rgba(255,255,255,0.18)" }
  }
};
